import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertTriangle, Repeat, Calendar, Clock, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

export default function RecurringSuccess() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const seriesId = searchParams.get("series_id");

  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("error"); return; }
    try {
      const { data: d } = await axios.get(`${API}/api/recurring/checkout-status/${sessionId}`, { withCredentials: true });
      if (d.payment_status === "paid") { setStatus("success"); setData(d); return; }
      if (d.status === "expired") { setStatus("expired"); return; }
      if (attempts < 8) setAttempts(a => a + 1);
      else setStatus("timeout");
    } catch {
      if (attempts < 8) setAttempts(a => a + 1);
      else setStatus("error");
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === "checking" && sessionId) {
      const timer = setTimeout(poll, attempts === 0 ? 500 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, poll, sessionId]);

  if (status === "checking") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
        <div data-testid="recurring-processing" className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <h1 className="text-xl font-extrabold text-slate-900 dark:text-white">Setting up your weekly lessons...</h1>
          <p className="text-sm text-slate-500">Confirming payment and getting them in the diary</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    const series = data?.series;
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
        <div data-testid="recurring-confirmed" className="max-w-sm w-full space-y-6 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">All set for the weeks ahead</h1>
            <p className="text-sm text-slate-500 mt-1">Same time, every week — in the diary and ready</p>
          </div>
          {series && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-3 text-left">
              <div className="flex items-center gap-3">
                <Repeat className="w-5 h-5 text-blue-500" />
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {series.day_name || "Weekly"}s at {fmtTime(series.start_time)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-violet-500" />
                <p className="text-sm text-slate-500">{series.duration === 60 ? "1 Hour" : series.duration === 90 ? "90 Min" : "2 Hours"} — {series.frequency}</p>
              </div>
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-emerald-500" />
                <p className="text-sm font-bold text-emerald-600">£{Number(series.price_per_lesson).toFixed(2)} per lesson</p>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400 leading-relaxed">Each lesson charges 72 hours before — so nothing catches you off guard. Pause or cancel anytime.</p>
          <div className="space-y-2">
            <Button data-testid="view-my-series" onClick={() => nav("/learn/book/recurring")} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl">See my schedule</Button>
            <Button data-testid="back-home" onClick={() => nav("/learn")} variant="outline" className="w-full h-12 rounded-2xl">Back home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
      <div data-testid="recurring-failed" className="max-w-sm w-full space-y-6 text-center">
        <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-10 h-10 text-amber-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">{status === "expired" ? "That session timed out" : "Payment didn't go through"}</h1>
        <p className="text-sm text-slate-500">{status === "expired" ? "No charge — just start again when you're ready." : "Something tripped up on the way. Let's try again."}</p>
        <Button onClick={() => nav("/learn/book/recurring")} className="w-full h-12 bg-blue-600 text-white rounded-2xl">Try again</Button>
      </div>
    </div>
  );
}
